// Loader for the render-only coastline/border polylines written by tools/data/build.mjs:
// [u32 polylineCount][u32 pointCount × polylineCount][i16 lon, i16 lat × points], little-endian,
// degrees × POLYLINE_QUANT. Parsed once at startup; malformed data throws GeoAssetError.

import coastlinesUrl from '../../assets/geo/coastlines.bin?url'
import bordersUrl from '../../assets/geo/borders.bin?url'
import { GeoAssetError } from '../errors'

/** Must match LINE_QUANT in tools/data/build.mjs. */
const POLYLINE_QUANT = 100
const U32_BYTES = 4
const POINT_BYTES = 4
const MIN_POLYLINE_POINTS = 2
const MAX_ABS_LON = 180
const MAX_ABS_LAT = 90

export interface PolylineSet {
  /** Points per polyline, in file order. */
  readonly pointCounts: Uint32Array
  /** Interleaved lon, lat in degrees for every point of every polyline. */
  readonly lonLat: Float32Array
  readonly totalPoints: number
  /** Line segments when every polyline is drawn as consecutive point pairs. */
  readonly totalSegments: number
}

export interface GlobeGeo {
  readonly coastlines: PolylineSet
  readonly borders: PolylineSet
}

export function parsePolylines(buffer: ArrayBuffer, assetName: string): PolylineSet {
  const fail = (message: string): never => {
    throw new GeoAssetError(`${assetName}: ${message}`)
  }
  const view = new DataView(buffer)
  if (buffer.byteLength < U32_BYTES) fail('shorter than its header')
  const polylineCount = view.getUint32(0, true)
  const headerBytes = U32_BYTES + U32_BYTES * polylineCount
  if (headerBytes > buffer.byteLength) fail(`header declares ${polylineCount} polylines past the end of the file`)

  const pointCounts = new Uint32Array(polylineCount)
  let totalPoints = 0
  for (let i = 0; i < polylineCount; i++) {
    const count = view.getUint32(U32_BYTES + U32_BYTES * i, true)
    if (count < MIN_POLYLINE_POINTS) fail(`polyline ${i} has ${count} points`)
    pointCounts[i] = count
    totalPoints += count
  }
  const expectedBytes = headerBytes + POINT_BYTES * totalPoints
  if (expectedBytes !== buffer.byteLength) fail(`expected ${expectedBytes} bytes, got ${buffer.byteLength}`)

  const lonLat = new Float32Array(totalPoints * 2)
  let offset = headerBytes
  for (let p = 0; p < totalPoints; p++) {
    const lon = view.getInt16(offset, true) / POLYLINE_QUANT
    const lat = view.getInt16(offset + 2, true) / POLYLINE_QUANT
    if (Math.abs(lon) > MAX_ABS_LON || Math.abs(lat) > MAX_ABS_LAT) fail(`point ${p} out of range (${lon}, ${lat})`)
    lonLat[p * 2] = lon
    lonLat[p * 2 + 1] = lat
    offset += POINT_BYTES
  }
  return { pointCounts, lonLat, totalPoints, totalSegments: totalPoints - polylineCount }
}

async function fetchPolylines(url: string, assetName: string): Promise<PolylineSet> {
  const response = await fetch(url)
  if (!response.ok) throw new GeoAssetError(`${assetName}: HTTP ${response.status} fetching ${url}`)
  return parsePolylines(await response.arrayBuffer(), assetName)
}

export async function loadGlobeGeo(): Promise<GlobeGeo> {
  const [coastlines, borders] = await Promise.all([
    fetchPolylines(coastlinesUrl, 'coastlines.bin'),
    fetchPolylines(bordersUrl, 'borders.bin'),
  ])
  return { coastlines, borders }
}
